import type { KnownRoute } from "@/Routing/KnownRoute";
import { resolveRouteRedirect } from "@/Routing/resolveRouteRedirect";
import {
  setRedirectUrl,
  useAuthentication,
} from "@/Slices/authenticationSlice";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

interface RouteFallbackProps {
  signedInRoutes: Array<KnownRoute>;
  openRoutes: Array<KnownRoute>;
}

/** Redirects any URL no screen would render for. Renders nothing itself. */
export function RouteFallback(props: RouteFallbackProps) {
  const { signedInRoutes, openRoutes } = props;
  const { isAuthenticated } = useAuthentication();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Losing the session after being signed in on this page load means they
  // signed out, and whoever signs in next on this browser shouldn't be sent
  // back to the page they were on. A session that expired mid-visit has
  // already saved its own way back (useSessionExpiry).
  const wasAuthenticated = useRef(isAuthenticated);

  useEffect(
    function () {
      const redirect = resolveRouteRedirect({
        pathname,
        search,
        isAuthenticated,
        signedInRoutes,
        openRoutes,
      });
      if (isAuthenticated) {
        wasAuthenticated.current = true;
      }
      if (redirect === undefined) {
        return;
      }
      if (redirect.afterSignIn !== undefined && !wasAuthenticated.current) {
        dispatch(setRedirectUrl(redirect.afterSignIn));
      }
      navigate(redirect.to, { replace: true });
    },
    // The route lists are rebuilt every render; they only change with the
    // signed-in user, which isAuthenticated already tracks.
    [pathname, search, isAuthenticated],
  );

  return <></>;
}
